'use client';

import { Suspense, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import { safeReturnTo } from '@/lib/safe-return-to';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Cake } from 'lucide-react';

function LoginForm() {
  const searchParams = useSearchParams();
  const returnTo = safeReturnTo(searchParams.get('returnTo'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const canUseDevAuth = process.env.NODE_ENV === 'development';
  const devAutoLogin = canUseDevAuth ? searchParams.get('dev') : null;

  // Development only: /login?dev=<email> signs in through the dev bypass with
  // no typing, so simulators and automated audits can reach signed-in routes.
  useEffect(() => {
    if (!devAutoLogin) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/proxy/auth/dev-login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: devAutoLogin.trim().toLowerCase(), password: 'dev' }),
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        document.cookie = `sweetspot_dev_auth=${encodeURIComponent(JSON.stringify({ userId: data.userId, email: data.email }))};path=/;max-age=${60 * 60 * 24 * 7}`;
        window.location.href = getRedirectUrl();
      } catch {
        /* dev only */
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devAutoLogin]);

  const getRedirectUrl = () => {
    if (returnTo) return returnTo;
    if (typeof window === 'undefined') return '/dashboard';
    const stored = safeReturnTo(sessionStorage.getItem('sweetspot_return_to'));
    sessionStorage.removeItem('sweetspot_return_to');
    return stored ?? '/dashboard';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const normalizedEmail = email.trim().toLowerCase();

    const supabase = createClient();
    const { error: supabaseError } = await supabase.auth.signInWithPassword({ email: normalizedEmail, password });

    if (!supabaseError) {
      window.location.href = getRedirectUrl();
      return;
    }

    if (!canUseDevAuth) {
      setError(supabaseError.message);
      setLoading(false);
      return;
    }

    try {
      const res = await fetch('/api/proxy/auth/dev-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail, password }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.message || supabaseError.message);
        setLoading(false);
        return;
      }

      const data = await res.json();
      if (!canUseDevAuth) {
        setError(supabaseError.message);
        setLoading(false);
        return;
      }

      document.cookie = `sweetspot_dev_auth=${encodeURIComponent(JSON.stringify({ userId: data.userId, email: data.email }))};path=/;max-age=${60 * 60 * 24 * 7}`;
      window.location.href = getRedirectUrl();
    } catch {
      setError('Unable to reach local auth server. Please ensure API is running on http://localhost:4000.');
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="text-center">
        <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <Cake className="h-6 w-6 text-primary" />
        </div>
        <CardTitle className="text-2xl">Welcome back</CardTitle>
        <CardDescription>Sign in to your Sweetspot account</CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
        </CardContent>
        <CardFooter className="flex flex-col gap-3">
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
          <p className="text-sm text-muted-foreground">
            Don&apos;t have an account?{' '}
            <Link href={returnTo ? `/signup?returnTo=${encodeURIComponent(returnTo)}` : '/signup'} className="text-primary hover:underline">
              Sign up
            </Link>
          </p>
        </CardFooter>
      </form>
    </Card>
  );
}

export default function LoginPage() {
  return (
    <Suspense>
      <LoginForm />
    </Suspense>
  );
}
